/**
 * 산점도 피팅 모델 + 상관 순위 (시각화 › 데이터 연관관계).
 * 외부 라이브러리 없이 최소제곱으로 계산한다. 결정계수 R² 는 모델마다 원래 y 척도에서 계산해 서로 비교 가능하다.
 */
import { MIN_VALID, pearson } from "./dvData";

export const FIT_MODELS = [
  { key: "linear", name: "선형", formula: "y = a + b·x" },
  { key: "log", name: "로그", formula: "y = a + b·ln(x)", cond: "x > 0" },
  { key: "quad", name: "2차", formula: "y = a + b·x + c·x²" },
  { key: "exp", name: "지수", formula: "y = a·e^(b·x)", cond: "y > 0" },
  { key: "power", name: "거듭제곱", formula: "y = a·x^b", cond: "x > 0, y > 0" },
];

/** y = a + b·x 최소제곱. 분산이 없으면 null */
function linreg(xs, ys) {
  const n = xs.length;
  let sx = 0, sy = 0, sxx = 0, sxy = 0;
  for (let i = 0; i < n; i++) { sx += xs[i]; sy += ys[i]; sxx += xs[i] * xs[i]; sxy += xs[i] * ys[i]; }
  const den = n * sxx - sx * sx;
  if (!isFinite(den) || Math.abs(den) < 1e-12 * Math.max(1, n * sxx)) return null;
  const b = (n * sxy - sx * sy) / den;
  return { a: (sy - b * sx) / n, b };
}

/** y = a + b·x + c·x² 최소제곱 (정규방정식 3×3, 부분 피벗 가우스 소거) */
function quadreg(xs, ys) {
  const n = xs.length;
  // 수치 안정성을 위해 x 를 평균 중심·척도 정규화한 뒤 역변환
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  let sd = Math.sqrt(xs.reduce((a, b) => a + (b - mx) * (b - mx), 0) / n);
  if (!(sd > 0)) return null;
  const zs = xs.map((x) => (x - mx) / sd);
  let s1 = 0, s2 = 0, s3 = 0, s4 = 0, sy = 0, szy = 0, sz2y = 0;
  for (let i = 0; i < n; i++) {
    const z = zs[i], z2 = z * z;
    s1 += z; s2 += z2; s3 += z2 * z; s4 += z2 * z2;
    sy += ys[i]; szy += z * ys[i]; sz2y += z2 * ys[i];
  }
  const M = [[n, s1, s2, sy], [s1, s2, s3, szy], [s2, s3, s4, sz2y]];
  for (let c = 0; c < 3; c++) {
    let p = c;
    for (let r = c + 1; r < 3; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
    if (Math.abs(M[p][c]) < 1e-12) return null;
    [M[c], M[p]] = [M[p], M[c]];
    for (let r = 0; r < 3; r++) {
      if (r === c) continue;
      const f = M[r][c] / M[c][c];
      for (let k = c; k < 4; k++) M[r][k] -= f * M[c][k];
    }
  }
  const A = M[0][3] / M[0][0], B = M[1][3] / M[1][1], C = M[2][3] / M[2][2]; // y = A + B z + C z²
  // z = (x - mx)/sd 역변환 → y = a + b x + c x²
  const c = C / (sd * sd);
  const b = B / sd - 2 * C * mx / (sd * sd);
  const a = A - B * mx / sd + C * mx * mx / (sd * sd);
  return { a, b, c };
}

function rSquared(ys, pred) {
  const n = ys.length;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let sst = 0, ssr = 0;
  for (let i = 0; i < n; i++) {
    if (!isFinite(pred[i])) return null;
    sst += (ys[i] - my) * (ys[i] - my);
    ssr += (ys[i] - pred[i]) * (ys[i] - pred[i]);
  }
  if (sst <= 0) return null;
  return 1 - ssr / sst;
}

/**
 * 한 모델을 점 집합에 피팅한다.
 * 반환: { key, n, ok, coef, r2, predict(x) } — 조건(x>0 등)에 안 맞는 점은 제외하고 n 에 반영.
 */
export function fitModel(points, key) {
  let pts = points.filter((p) => isFinite(p.x) && isFinite(p.y));
  if (key === "log" || key === "power") pts = pts.filter((p) => p.x > 0);
  if (key === "exp" || key === "power") pts = pts.filter((p) => p.y > 0);
  const n = pts.length;
  const base = { key, n, ok: false, coef: null, r2: null, predict: null };
  if (n < MIN_VALID) return base;
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  let coef, predict;
  if (key === "linear") {
    const f = linreg(xs, ys); if (!f) return base;
    coef = [f.a, f.b]; predict = (x) => f.a + f.b * x;
  } else if (key === "log") {
    const f = linreg(xs.map(Math.log), ys); if (!f) return base;
    coef = [f.a, f.b]; predict = (x) => (x > 0 ? f.a + f.b * Math.log(x) : NaN);
  } else if (key === "quad") {
    const f = quadreg(xs, ys); if (!f) return base;
    coef = [f.a, f.b, f.c]; predict = (x) => f.a + f.b * x + f.c * x * x;
  } else if (key === "exp") {
    const f = linreg(xs, ys.map(Math.log)); if (!f) return base;
    const a = Math.exp(f.a), b = f.b;
    coef = [a, b]; predict = (x) => a * Math.exp(b * x);
  } else if (key === "power") {
    const f = linreg(xs.map(Math.log), ys.map(Math.log)); if (!f) return base;
    const a = Math.exp(f.a), b = f.b;
    coef = [a, b]; predict = (x) => (x > 0 ? a * Math.pow(x, b) : NaN);
  } else {
    return base;
  }
  const r2 = rSquared(ys, xs.map(predict));
  if (r2 === null || !isFinite(r2)) return base;
  return { key, n, ok: true, coef, r2, predict };
}

/** 모든 모델 피팅 결과(모델 메타 포함) */
export function fitAll(points) {
  return FIT_MODELS.map((m) => ({ ...m, ...fitModel(points, m.key) }));
}

/** R² 가 가장 높은 모델(없으면 null) */
export function bestFit(points) {
  const ok = fitAll(points).filter((f) => f.ok);
  return ok.length ? ok.reduce((a, b) => (b.r2 > a.r2 ? b : a)) : null;
}

/** 계수 표기 (유효숫자 4자리, 큰/작은 값은 지수표기) */
export function fmtCoef(v) {
  if (v === null || v === undefined || !isFinite(v)) return "–";
  const a = Math.abs(v);
  if (a !== 0 && (a >= 1e5 || a < 1e-3)) return v.toExponential(3);
  return Number(v.toPrecision(4)).toString();
}

/** 피팅식 문자열 */
export function fitEquation(f) {
  if (!f || !f.ok) return "";
  const [a, b, c] = f.coef;
  const sgn = (v) => (v < 0 ? "− " + fmtCoef(-v) : "+ " + fmtCoef(v));
  switch (f.key) {
    case "linear": return `y = ${fmtCoef(a)} ${sgn(b)}·x`;
    case "log": return `y = ${fmtCoef(a)} ${sgn(b)}·ln(x)`;
    case "quad": return `y = ${fmtCoef(a)} ${sgn(b)}·x ${sgn(c)}·x²`;
    case "exp": return `y = ${fmtCoef(a)}·e^(${fmtCoef(b)}·x)`;
    case "power": return `y = ${fmtCoef(a)}·x^${fmtCoef(b)}`;
    default: return "";
  }
}

/**
 * 두 속성의 공통 표본 분포 지표.
 *  n          공통 표본 수
 *  distinctX/Y 공통 표본 안의 서로 다른 값 수
 *  varX/Y     변동 표본 수 = n − 최빈값 빈도 (최빈값이 아닌 값을 가진 표본 수)
 *  varMin     min(varX, varY) — 쌍의 실질적 변동 표본 수
 * 값이 거의 한 가지인 열은 n 이 커도 varMin 이 작아 상관이 과대평가된 쌍을 걸러낼 수 있다.
 */
export function diversity(pa, pb) {
  const va = pa.values || {}, vb = pb.values || {};
  const cx = new Map(), cy = new Map();
  let n = 0;
  for (const k of Object.keys(va)) {
    if (vb[k] === undefined) continue;
    n++;
    cx.set(va[k], (cx.get(va[k]) || 0) + 1);
    cy.set(vb[k], (cy.get(vb[k]) || 0) + 1);
  }
  const modeX = Math.max(0, ...cx.values()), modeY = Math.max(0, ...cy.values());
  const varX = n - modeX, varY = n - modeY;
  return { n, distinctX: cx.size, distinctY: cy.size, varX, varY, varMin: Math.min(varX, varY) };
}

const normName = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

/**
 * 상관 순위: 분석 가능 속성 전체 쌍 중 |r| 상위.
 * minN: 공통 표본 하한, hidePerfect: |r|≥0.999(동일 컬럼·테스트값) 숨김,
 * hideSameName: 이름이 같은 속성 쌍(예: n형 층·p형 층의 Channel width) 숨김, limit: 반환 개수
 */
export function rankPairs(props, { minN = 10, minVar = 10, hidePerfect = true, hideSameName = true, limit = 20 } = {}) {
  const out = [];
  for (let i = 0; i < props.length; i++) {
    for (let j = i + 1; j < props.length; j++) {
      if (hideSameName && normName(props[i].name) === normName(props[j].name)) continue;
      const { r, n } = pearson(props[i], props[j]);
      if (r === null || n < minN) continue;
      if (hidePerfect && Math.abs(r) >= 0.999) continue;
      const dv = diversity(props[i], props[j]);
      if (dv.varMin < minVar) continue;
      out.push({ a: props[i], b: props[j], r, n, dv });
    }
  }
  out.sort((p, q) => Math.abs(q.r) - Math.abs(p.r) || q.dv.varMin - p.dv.varMin || q.n - p.n);
  return out.slice(0, limit);
}
